/**
 * Firebase compatibility layer — UPDATED FOR CLERK AUTH.
 *
 * This file exports the same names that components expect (db, auth, storage, etc.)
 * but they are now backed by Clerk (auth) and the new API service.
 *
 * Components should NOT need to change their imports from "../firebase".
 * The actual API calls (ref, onValue, etc.) are handled by the shim modules:
 *   - src/lib/firebase-database.js  (replaces "firebase/database")
 *   - src/lib/firebase-auth.js      (replaces "firebase/auth")
 *   - src/lib/firebase-storage.js   (replaces "firebase/storage")
 */

const AUTH_CHANGED_EVENT = 'appwrite-auth-changed';

function emitAuthChanged(reason = 'updated') {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT, { detail: { reason } }));
    localStorage.setItem('vk_auth_changed_at', `${Date.now()}`);
  } catch (err) {
    console.warn('emitAuthChanged warning:', err);
  }
}

// Database proxy — the actual operations happen in firebase-database.js
// Components pass `db` to `ref(db, path)` but the shim ignores it.
const db = { _type: 'appwrite-db' };

// Auth proxy — now backed by Clerk
const auth = {
  get currentUser() {
    return auth._cachedUser;
  },
  _cachedUser: null,
  signOut: async () => {
    try {
      const clerk = (window).Clerk;
      if (clerk) await clerk.signOut();
      auth._cachedUser = null;
      emitAuthChanged('logout');
    } catch (err) {
      console.error('signOut error:', err);
    }
  },
  // v8-style onAuthStateChanged for compatibility
  onAuthStateChanged: (callback) => {
    const notify = () => {
      const clerk = (window).Clerk;
      if (clerk && clerk.user) {
        const u = clerk.user;
        auth._cachedUser = {
          uid: u.id,
          email: u.primaryEmailAddress?.emailAddress || '',
          displayName: u.fullName || u.firstName || '',
          photoURL: u.imageUrl || '',
          emailVerified: u.primaryEmailAddress?.verification?.status === 'verified',
        };
        callback(auth._cachedUser);
      } else {
        auth._cachedUser = null;
        callback(null);
      }
    };

    // Delay initial check to allow Clerk to initialize
    setTimeout(notify, 100);
    const onChanged = () => notify();
    window.addEventListener(AUTH_CHANGED_EVENT, onChanged);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, onChanged);
    };
  },
};

// Initialize auth state from Clerk
setTimeout(() => {
  const clerk = (window).Clerk;
  if (clerk && clerk.user) {
    const u = clerk.user;
    auth._cachedUser = {
      uid: u.id,
      email: u.primaryEmailAddress?.emailAddress || '',
      displayName: u.fullName || u.firstName || '',
      photoURL: u.imageUrl || '',
      emailVerified: u.primaryEmailAddress?.verification?.status === 'verified',
    };
    emitAuthChanged('init-user');
  } else {
    auth._cachedUser = null;
    emitAuthChanged('init-null');
  }
}, 100);

// Storage proxy — the actual operations happen in firebase-storage.js
const storage = { _type: 'appwrite-storage' };

// Firestore proxy (minimal usage — only in server/villaform.js)
const firestore = { _type: 'appwrite-firestore' };

// Messaging — not available (FCM discontinued)
const messaging = null;

// Stubs for Firebase-specific exports
const RecaptchaVerifier = class {};
const getToken = async () => null;
const onMessage = () => () => {};
const isSupported = async () => false;
const functions = {};
const app = {};

// Notification permission stub (FCM not available)
export const requestNotificationPermission = async () => {
  console.log('Push notifications (FCM) are not available.');
  return null;
};

export { app, auth, firestore, db, messaging, storage, RecaptchaVerifier, getToken, onMessage, isSupported, functions }
